import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SmartLink } from "./SmartLink";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_URL, SOCIALS } from "../data/content";
import { CITIES } from "../data/cities";
import {
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Music2,
  Send,
  MessageCircle,
  MapPin,
  Mail,
  Building2,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  Zap,
  Users,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const API = `${window.__BACKEND__}/api`;

// Official Threads glyph (monochrome) — matches the lucide stroke icons in the footer.
function ThreadsIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" aria-hidden="true">
      <path d="M141.537 88.988c-.827-.396-1.667-.778-2.518-1.143-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.694 14.724-10.548 21.347-10.548h.23c8.25.053 14.475 2.451 18.504 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.141-23.82 1.372-39.134 15.265-38.105 34.569.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C29.236 139.966 23.808 120.682 23.605 96c.203-24.682 5.631-43.966 16.134-57.317C50.954 24.425 68.204 17.11 91.013 16.94c22.975.171 40.526 7.521 52.171 21.848 5.71 7.026 10.015 15.861 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C141.036 9.607 119.202.195 91.07 0h-.113C62.882.194 41.292 9.642 26.788 28.079 13.882 44.486 7.224 67.316 7.001 95.933L7 96l.001.067c.223 28.617 6.881 51.447 19.787 67.854C41.292 182.358 62.882 191.806 90.957 192h.113c24.96-.173 42.554-6.708 57.048-21.192 18.963-18.943 18.392-42.693 12.142-57.266-4.484-10.455-13.033-18.946-24.723-24.554zM98.44 129.507c-10.44.588-21.286-4.098-21.82-14.135-.397-7.442 5.296-15.746 22.461-16.735 1.966-.114 3.895-.169 5.79-.169 6.235 0 12.068.606 17.371 1.765-1.978 24.702-13.58 28.713-23.802 29.274z"/>
    </svg>
  );
}

// Official TikTok glyph (monochrome) — lucide has no TikTok icon.
function TikTokIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
    </svg>
  );
}

const ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  threads: ThreadsIcon,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: TikTokIcon,
  spotify: Music2,
};

const PRODUCTS = [
  { label: "HostClub premium", href: "/hostclub" },
  { label: "LT.it — contratti & fiscalità", href: "https://contratti.vitadahost.it/", ext: true },
  { label: "Hostland — sito + guestbook", href: "/hostland" },
  { label: "ZeroCommissioni — prenota senza commissioni", href: "/zerocommissioni" },
  { label: "HostLab — formazione LIVE", href: "/servizi/hostlab" },
  { label: "HostMarket — piazza pro", href: "/servizi/hostmarket" },
  { label: "CIN Facile", href: "/servizi/cin-facile" },
  { label: "FaqBnb — manuale 2026", href: "/servizi/faqbnb" },
  { label: "Host & Furious podcast", href: "/servizi/podcast" },
];

const SERVICES_LINKS = [
  { label: "Chi siamo", href: "/chi-siamo", featured: true },
  // Calcolatore — link SEO con anchor text ad alto valore
  { label: "QuantoGuadagni? Calcolatore Affitti Brevi vs Tradizionale", href: "/calcolatoreguadagniaffittibrevi", featured: true, calc: true },
  { label: "Quanto guadagna un host? Calcolo netto online Airbnb/Booking", href: "/calcolatoreguadagniaffittibrevi", calc: true },
  { label: "Blog", href: "/blog" },
  { label: "Consulenze affitti brevi", href: "/consulenze" },
  { label: "Servizi & partner", href: "/servizi" },
  { label: "Lavora con noi — pubblicità, partnership, HostMarket", href: "/contatti/collabora" },
  { label: "Negozio digitale", href: "/negozio" },
  { label: "App mobile iOS", href: "/app" },
  { label: "Supportarci", href: "/supportarci" },
  { label: "Ebook FaqBnb (gratis)", href: "/servizi/faqbnb" },
  { label: "Targhe CIN 3D", href: "/negozio" },
];

const MAIN_CITIES = CITIES.slice(0, 12); // top 12 for footer

// ─── Footer Newsletter Box ─────────────────────────────────────
// Full-width sign-up card sotto la griglia di link. Copy dedicato:
// 1 email il lunedì mattina, zero spam, disiscrizione 1 click.
// Sync su Brevo tramite `POST /api/blog-newsletter/subscribe`.
function FooterNewsletter() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Inserisci un'email valida");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/blog-newsletter/subscribe`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
      });
      setStatus("done");
      toast.success("Iscritto! Grazie ✨");
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
    }
  };

  if (status === "done") {
    return (
      <div
        className="rounded-3xl p-6 md:p-8 border-2 mb-10 relative overflow-hidden"
        style={{ borderColor: "#4ade80", background: "linear-gradient(135deg, rgba(74,222,128,0.08), rgba(13,13,15,0.9))" }}
        data-testid="footer-newsletter-success"
      >
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex-none flex items-center justify-center">
            <CheckCircle2 size={28} className="text-emerald-400" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="font-display text-xl md:text-2xl text-white mb-1">Iscritto. Ci sentiamo presto.</div>
            <p className="text-white/60 text-sm">
              Ti abbiamo aggiunto alla newsletter di Vita da Host. <b className="text-white">Ti scriviamo solo quando è veramente utile</b>, e <b className="text-white">puoi cancellarti in un click quando vuoi</b>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-3xl mb-10 relative overflow-hidden border-2"
      style={{
        borderColor: "rgba(255,79,207,0.5)",
        background: "linear-gradient(135deg, rgba(255,79,207,0.10) 0%, rgba(234,88,12,0.05) 50%, rgba(96,165,250,0.05) 100%)",
        boxShadow: "0 30px 80px -30px rgba(255,79,207,0.35)",
      }}
      data-testid="footer-newsletter"
    >
      {/* decorative blobs */}
      <div
        className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(circle, rgba(255,79,207,0.5) 0%, transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full blur-3xl opacity-35"
        style={{ background: "radial-gradient(circle, rgba(234,88,12,0.4) 0%, transparent 70%)" }}
      />

      <div className="relative p-6 md:p-8 lg:p-10 grid lg:grid-cols-[1.05fr_1fr] gap-6 lg:gap-10 items-center">
        {/* LEFT — copy */}
        <div>
          <div className="text-[10px] font-tech uppercase tracking-[0.3em] text-[var(--vdh-pink)] mb-3 inline-flex items-center gap-2">
            <Sparkles size={12} /> Newsletter Vita da Host
          </div>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl leading-[1.05] tracking-tight text-white mb-3">
            Non perderti <span className="neon-yellow">niente di quello che facciamo.</span>
          </h3>
          <p className="text-white/80 text-[14.5px] leading-relaxed mb-2">
            <b className="text-white">Iscriviti alla newsletter di Vita da Host</b> per restare informato su news, articoli, guide, podcast e aggiornamenti normativi (CIN, cedolare, Airbnb 15,5%, sentenze). <b className="text-white">Ti scriviamo solo quando è veramente utile</b>, e <b className="text-white">puoi cancellarti in un click quando vuoi</b>.
          </p>
          <p className="text-white/50 text-[12.5px] leading-relaxed">
            GDPR compliant · nessuna cessione dati.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-tech uppercase tracking-widest bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
              <ShieldCheck size={11} /> Zero spam
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-tech uppercase tracking-widest bg-[var(--vdh-yellow)]/10 text-[var(--vdh-yellow)] border border-[var(--vdh-yellow)]/30">
              <Zap size={11} /> Solo se utile
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-tech uppercase tracking-widest bg-[var(--vdh-pink)]/10 text-[var(--vdh-pink)] border border-[var(--vdh-pink)]/30">
              <Users size={11} /> Community host
            </span>
          </div>
        </div>

        {/* RIGHT — form */}
        <form onSubmit={submit} className="grid gap-2.5" data-testid="footer-newsletter-form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Il tuo nome (facoltativo)"
            disabled={status === "sending"}
            className="px-4 py-3.5 rounded-2xl bg-[#0c0c10]/85 border border-white/10 focus:border-[var(--vdh-pink)]/60 text-sm text-white placeholder-white/40 outline-none disabled:opacity-60"
            data-testid="footer-newsletter-name"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="La tua email *"
            disabled={status === "sending"}
            className="px-4 py-3.5 rounded-2xl bg-[#0c0c10]/85 border border-white/10 focus:border-[var(--vdh-pink)]/60 text-sm text-white placeholder-white/40 outline-none disabled:opacity-60"
            data-testid="footer-newsletter-email"
          />
          <button
            type="submit"
            disabled={status === "sending"}
            className="inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-base transition-all disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, var(--vdh-pink), #ff8a00)",
              color: "#0a0a10",
              boxShadow: "0 8px 26px -8px rgba(255,79,207,0.75), 0 0 0 1px rgba(255,255,255,0.15) inset",
            }}
            data-testid="footer-newsletter-submit"
          >
            {status === "sending" ? (
              <><Loader2 size={16} className="animate-spin" /> Iscrivo…</>
            ) : (
              <><Mail size={16} /> Iscrivimi</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Footer() {
  // Inject LocalBusiness JSON-LD into head once (footer presence = on every page)
  useEffect(() => {
    const id = "ldld-localbusiness";
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.vitadahost.it/#organization",
      name: "Vita da Host",
      legalName: "Vita da Host",
      url: "https://www.vitadahost.it",
      logo: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/kongmzn7_logo%20nero.png",
      image: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/kongmzn7_logo%20nero.png",
      description: "Il portale italiano per host e property manager del settore affitti brevi. Corsi live, consulenze 1:1, strumenti, community.",
      email: "vitadahost@gmail.com",
      vatID: "IT02903680995",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Genova",
        addressRegion: "Liguria",
        postalCode: "16121",
        addressCountry: "IT",
      },
      areaServed: { "@type": "Country", name: "Italia" },
      knowsAbout: [
        "affitti brevi", "locazione turistica", "casa vacanze", "bed and breakfast",
        "CIN", "cedolare secca", "Airbnb", "Booking.com", "property management",
        "channel manager", "revenue management", "alloggiati web", "BDSR",
      ],
      priceRange: "€",
      sameAs: [
        "https://www.instagram.com/vita_da_host_/",
        "https://www.facebook.com/VitadaHostUfficiale/",
        "https://www.tiktok.com/@vita.da.host",
        "https://www.threads.com/@vita_da_host_",
        "https://youtube.com/@vitadahostchannel",
        "https://www.linkedin.com/in/sergio-battelli-vitadahost/",
        "https://open.spotify.com/show/0Z0ZbTupFc67dxRVUEH5so",
      ],
    });
    document.head.appendChild(script);
  }, []);

  return (
    <footer
      data-testid="site-footer"
      itemScope
      itemType="https://schema.org/LocalBusiness"
      className="relative vdh-safe-x vdh-safe-bottom"
      style={{ background: "#111318", color: "#D3D7DE" }}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 pb-8">
        {/* TOP: brand + columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-10 text-[14px]" style={{ lineHeight: 1.9 }}>
          {/* BRAND BLOCK */}
          <div className="flex flex-col gap-2.5" style={{ lineHeight: 1.5 }}>
            <strong style={{ color: "#FFFFFF", fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: 22 }} itemProp="name">Vita da Host</strong>
            <span itemProp="description" style={{ color: "#D3D7DE" }}>
              Il portale italiano per host e property manager di affitti brevi. Da host, per host.
            </span>
            <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress" style={{ color: "#A7ACB6" }}>
              <span itemProp="addressLocality">Genova</span>, <span itemProp="addressRegion">Liguria</span> · P.IVA <span itemProp="vatID">02903680995</span>
            </span>
            <a href="mailto:vitadahost@gmail.com" itemProp="email" className="hover:opacity-80" style={{ color: "#FFFFFF" }}>vitadahost@gmail.com</a>
            {/* Socials */}
            <div className="flex flex-wrap gap-2 mt-2">
              {SOCIALS.map((s) => {
                const Icon = ICONS[s.icon] || Send;
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    data-testid={`social-${s.icon}`}
                    aria-label={s.name}
                    itemProp="sameAs"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: "#1E2129", color: "#D3D7DE" }}
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* SERVIZI */}
          <nav className="flex flex-col" aria-labelledby="foot-serv">
            <strong id="foot-serv" style={{ color: "#FFFFFF", marginBottom: 6 }}>Servizi</strong>
            <a href="/hostland" data-testid="footer-hostland" style={{ color: "#D3D7DE" }}>Hostland</a>
            <a href="/zerocommissioni" data-testid="footer-zerocommissioni" style={{ color: "#D3D7DE" }}>ZeroCommissioni, prenota senza commissioni</a>
            <a href="/hostland#welcome-book" data-testid="footer-welcome-book-digitale" style={{ color: "#D3D7DE" }}>Welcome book digitale</a>
            <a href="/hostland#sito-prenotazioni-dirette" data-testid="footer-sito-bb" style={{ color: "#D3D7DE" }}>Sito per B&amp;B senza commissioni</a>
            <a href="https://hostland.vitadahost.it/vienidanoi" target="_blank" rel="noopener noreferrer" data-testid="footer-vienidanoi" style={{ color: "#D3D7DE" }}>Vieni da Noi ora è Hostland</a>
            {[
              ["Consulenze", "/consulenze"],
              ["Cantiere Host", "/cantierehost"],
              ["HostLab", "/hostlab"],
              ["CIN Facile", "/servizi/cin-facile"],
              ["HostMarket", "/servizi/hostmarket"],
              ["HostClub", "/hostclub"],
            ].map(([label, to]) => (
              <SmartLink key={label} to={to} className="hover:opacity-100" style={{ color: "#D3D7DE" }}>{label}</SmartLink>
            ))}
          </nav>

          {/* RISORSE */}
          <nav className="flex flex-col" aria-labelledby="foot-res">
            <strong id="foot-res" style={{ color: "#FFFFFF", marginBottom: 6 }}>Risorse</strong>
            <Link to="/guestbook-digitale" data-testid="footer-welcomebook" style={{ color: "#D3D7DE" }}>Welcome book digitale</Link>
            <Link to="/regole-della-casa-airbnb" data-testid="footer-regole-casa" style={{ color: "#D3D7DE" }}>Regole della casa Airbnb</Link>
            <Link to="/qr-code-wifi-ospiti" data-testid="footer-qr-wifi" style={{ color: "#D3D7DE" }}>QR code Wi-Fi ospiti</Link>
            <Link to="/blog" style={{ color: "#D3D7DE" }}>Blog</Link>
            <Link to="/hostnews" style={{ color: "#D3D7DE" }}>HostNews</Link>
            <Link to="/hostshow" data-testid="footer-hostshow" style={{ color: "#D3D7DE" }}>HostShow</Link>
            <Link to="/newsletter" data-testid="footer-newsletter-link" style={{ color: "#D3D7DE" }}>Newsletter</Link>
            <Link to="/radar" data-testid="footer-radar-link" style={{ color: "#D3D7DE" }}>Radar dei vettori</Link>
            <Link to="/servizi/podcast" style={{ color: "#D3D7DE" }}>Podcast Host &amp; Furious</Link>
            <Link to="/servizi/faqbnb" style={{ color: "#D3D7DE" }}>FaqBnb</Link>
            <Link to="/calcolatoreguadagniaffittibrevi" data-testid="footer-calc" style={{ color: "#D3D7DE" }}>Calcolatore guadagni</Link>
            <Link to="/negozio" style={{ color: "#D3D7DE" }}>Negozio</Link>
            <Link to="/chi-siamo" data-testid="footer-chi-siamo" style={{ color: "#D3D7DE" }}>Chi siamo</Link>
            <Link to="/contatti/collabora" style={{ color: "#D3D7DE" }}>Lavora con noi</Link>
          </nav>

          {/* CITIES SEO HUB */}
          <nav className="flex flex-col" aria-labelledby="foot-cities">
            <strong id="foot-cities" style={{ color: "#FFFFFF", marginBottom: 6 }}>Affitti brevi per città</strong>
            <div className="grid grid-cols-2 gap-x-3" style={{ lineHeight: 1.9 }}>
              {MAIN_CITIES.map((c) => (
                <Link key={c.slug} to={`/affitti-brevi/${c.slug}`} title={`Affitti brevi ${c.name} 2026 — Vita da Host`} style={{ color: "#D3D7DE" }}>
                  {c.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* BOTTOM LEGAL BAR */}
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-[18px] pb-7 flex flex-wrap gap-x-[18px] gap-y-2 text-[13px]" style={{ borderTop: "1px solid #2A2E37", color: "#A7ACB6" }}>
        <span>© {new Date().getFullYear()} Vita da Host</span>
        <Link to="/privacy" data-testid="footer-privacy" style={{ color: "#D3D7DE" }}>Privacy</Link>
        <Link to="/cookie-policy" data-testid="footer-cookie" style={{ color: "#D3D7DE" }}>Cookie</Link>
        <Link to="/termini-vendita" data-testid="footer-termini-vendita" style={{ color: "#D3D7DE" }}>Termini di vendita</Link>
        <Link to="/recesso" data-testid="footer-recesso" style={{ color: "#D3D7DE" }}>Recesso</Link>
        <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" style={{ color: "#D3D7DE" }}>Sitemap</a>
        <a href="https://www.vienidanoi.it" target="_blank" rel="noopener noreferrer" data-testid="footer-vdn" style={{ color: "#D3D7DE" }}>vienidanoi.it</a>
      </div>
    </footer>
  );
}
